import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { tokens, ToggleGroup, Text, Tooltip, Loader, Icon } from '@hotjar/hotsauce';
import type { IconSetDefinitions } from 'packages/hotsauce/build/components/Icon/icons';
import { abbrNumber } from '@hotjar/insights-common/lib/number';
import { plural } from '@hotjar/insights-common/lib/plural';
import { HEATMAP_DATA_TYPES, HEATMAP_DEVICE_TYPES, SMART_PANEL } from '@hotjar/heatmaps';
import { useGetDeviceSessions } from '@hotjar/webapp/domains/heatmaps/hooks/useGetDeviceSessions';
import type { ModelState } from '@hotjar/webapp/features/Heatmaps/ContinuousCaptureReport/Report/ReportTypes';
import { useFeatureFlag } from '@hotjar/webapp/hooks/useFeatureFlag';
import { capitalize } from '@hotjar/insights-common/lib/string';

import { WIDGET_Z_INDEX } from './constants';
import { OptionsWidget as LegacyOptionsWidget } from './LegacyOptionsWidget';

const { DESKTOP, TABLET, PHONE } = HEATMAP_DEVICE_TYPES;

const ToggleGroupContainer = styled.div`
  pointer-events: auto;
  margin-top: auto;
  display: flex;
  flex-direction: row;
  position: fixed;
  bottom: ${tokens.spacer.x2};
  margin-left: ${tokens.spacer.x2};
  z-index: ${WIDGET_Z_INDEX};
  gap: ${tokens.spacer.x1};
`;

interface OptionsWidgetProps {
  reportState: ModelState; // TODO: To be deleted when the FF for the smart panel is cleaned-up.
  clickCount?: number; // TODO: To be deleted when the FF for the smart panel is cleaned-up.
  selectedDeviceType: HeatmapDeviceTypes;
  selectedHeatType: HeatmapDataType;
  onDeviceTypeChange: (type: HeatmapDeviceTypes) => void;
  onHeatTypeChange: (type: HeatmapDataType) => void;
}

const DeviceToggleItem = ({
  device,
  label,
  iconName,
  sessionsCount,
  isFetching,
  isError,
}: {
  device: typeof DESKTOP | typeof TABLET | typeof PHONE;
  label: string;
  iconName: keyof IconSetDefinitions;
  sessionsCount: number;
  isFetching: boolean;
  isError: boolean;
}) => {
  const { t } = useTranslation('heatmaps');
  let deviceText = '';
  switch (device) {
    case 'desktop':
      deviceText = t('general-terms.device-desktop');
      break;
    case 'tablet':
      deviceText = t('general-terms.device-tablet');
      break;
    case 'phone':
      deviceText = t('general-terms.device-phone');
      break;

    default:
      break;
  }

  let tooltipText = `${abbrNumber(sessionsCount)} ${deviceText} ${plural(
    { one: t('session-counts.session-text'), other: t('session-counts.sessions-text') },
    sessionsCount,
  )}`;
  if (isError) tooltipText = t('options-widget.sessions-num-unavailable');

  return (
    <Tooltip isDisabled={isFetching} text={tooltipText}>
      <span
        aria-label={label}
        css={css`
          display: flex;
        `}
      >
        <Icon
          icon={iconName}
          size="m"
          css={css`
            margin-right: ${tokens.spacer.x1};
          `}
        />
        {isFetching ? (
          <Loader size="m" />
        ) : (
          <Text as="span" textType="caption">{`${isError ? '-' : abbrNumber(sessionsCount)}`}</Text>
        )}
      </span>
    </Tooltip>
  );
};

export function OptionsWidget({
  reportState, // TODO: To be deleted when the FF for the smart panel is cleaned-up.
  clickCount, // TODO: To be deleted when the FF for the smart panel is cleaned-up.
  selectedDeviceType,
  selectedHeatType,
  onDeviceTypeChange,
  onHeatTypeChange,
}: OptionsWidgetProps): JSX.Element {
  const { t } = useTranslation('heatmaps');
  const isSmartPanelEnabled = useFeatureFlag(SMART_PANEL);
  const { isFetching, sessions, isError } = useGetDeviceSessions();

  // TODO: To be deleted when the FF for the smart panel is cleaned-up.
  if (!isSmartPanelEnabled)
    return (
      <LegacyOptionsWidget
        reportState={reportState}
        clickCount={clickCount}
        selectedDeviceType={selectedDeviceType}
        selectedHeatType={selectedHeatType}
        onDeviceTypeChange={onDeviceTypeChange}
        onHeatTypeChange={onHeatTypeChange}
      />
    );

  return (
    <ToggleGroupContainer>
      <ToggleGroup
        description={t('options-widget.device-type')}
        orientation="horizontal"
        onChange={(selectedType) => onDeviceTypeChange(selectedType as HeatmapDeviceTypes)}
        selectedValue={selectedDeviceType}
      >
        <ToggleGroup.Toggle value={DESKTOP} key={DESKTOP}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={DESKTOP}
            label={capitalize(t('general-terms.device-desktop'))}
            iconName="device_desktop"
            sessionsCount={sessions[DESKTOP]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>

        <ToggleGroup.Toggle value={TABLET} key={TABLET}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={TABLET}
            label={capitalize(t('general-terms.device-tablet'))}
            iconName="device_tablet"
            sessionsCount={sessions[TABLET]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>
        <ToggleGroup.Toggle value={PHONE} key={PHONE}>
          {/* @ts-expect-error until DS supports an Element, not just a string */}
          <DeviceToggleItem
            device={PHONE}
            label={capitalize(t('general-terms.device-phone'))}
            iconName="device_phone"
            sessionsCount={sessions[PHONE]}
            isFetching={isFetching}
            isError={isError}
          />
        </ToggleGroup.Toggle>
      </ToggleGroup>

      {selectedDeviceType === DESKTOP ? (
        <ToggleGroup
          description={t('options-widget.heat-type')}
          orientation="horizontal"
          onChange={(selectedType) => onHeatTypeChange(selectedType as HeatmapDataType)}
          selectedValue={selectedHeatType}
        >
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.CLICK}
            icon="cursor_click"
            tooltipText={capitalize(t('general-terms.click'))}
            description={capitalize(t('general-terms.click'))}
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.MOVEMENT}
            icon="cursor_move"
            tooltipText={capitalize(t('general-terms.move'))}
            description={capitalize(t('general-terms.move'))}
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.SCROLL}
            icon="scroll"
            tooltipText={capitalize(t('general-terms.scroll'))}
            description={capitalize(t('general-terms.scroll'))}
          />
        </ToggleGroup>
      ) : (
        <ToggleGroup
          description={t('options-widget.heat-type')}
          orientation="horizontal"
          onChange={(selectedType) => onHeatTypeChange(selectedType as HeatmapDataType)}
          selectedValue={selectedHeatType}
        >
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.CLICK}
            icon="finger_tap"
            tooltipText={capitalize(t('general-terms.tap'))}
            description={capitalize(t('general-terms.tap'))}
          />
          <ToggleGroup.IconToggle
            value={HEATMAP_DATA_TYPES.SCROLL}
            icon="scroll"
            tooltipText={capitalize(t('general-terms.scroll'))}
            description={capitalize(t('general-terms.scroll'))}
          />
        </ToggleGroup>
      )}
    </ToggleGroupContainer>
  );
}
